// src/components/common/useDialogFocus.js
//
// Gestión de foco de un diálogo modal: al abrir enfoca el primer campo (o el
// elemento marcado con data-autofocus), mantiene Tab dentro del diálogo y, al
// cerrar, devuelve el foco al elemento que lo abrió. Escape llama onEscape.
import { useEffect, useRef } from 'react';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const focusables = node => Array.from(node.querySelectorAll(FOCUSABLE)).filter(el => !el.hidden && !el.closest('[hidden]'));

export default function useDialogFocus(ref, { onEscape } = {}) {
  const escapeRef = useRef(onEscape);
  escapeRef.current = onEscape;

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const opener = document.activeElement;
    const first = node.querySelector('[data-autofocus]')
      || node.querySelector('input:not([disabled]):not([type="hidden"]):not([type="file"]), select:not([disabled]), textarea:not([disabled])')
      || focusables(node)[0]
      || node;
    first.focus({ preventScroll: true });
    return () => {
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
    };
  }, [ref]);

  // Se usa como onKeyDown del diálogo (bubbling de React): un diálogo hijo
  // (p. ej. la confirmación de descartar) vive fuera de este nodo y no se mezcla.
  return event => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      escapeRef.current?.(event);
      return;
    }
    if (event.key !== 'Tab') return;
    const node = ref.current;
    if (!node) return;
    const items = focusables(node);
    if (items.length === 0) { event.preventDefault(); node.focus(); return; }
    const firstItem = items[0];
    const lastItem = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === firstItem || document.activeElement === node)) {
      event.preventDefault();
      lastItem.focus();
    } else if (!event.shiftKey && document.activeElement === lastItem) {
      event.preventDefault();
      firstItem.focus();
    }
  };
}
